import React, { useEffect, useRef } from 'react';
import { X, Loader2, CheckCircle2, XCircle, Info } from 'lucide-react';

function stageLabel(stage) {
  const map = {
    starting: 'Starting',
    preparing: 'Preparing',
    analyzing: 'Analyzing',
    attribute_complete: 'Attribute done',
    synthesizing: 'Summarizing',
    complete: 'Complete',
    error: 'Error',
  };
  return map[stage] || stage || 'Update';
}

/**
 * Live log for an in-flight attribute test run (SSE progress from AI service).
 */
export default function TestRunProgressModal({ progress, onClose }) {
  const logEndRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [progress?.messages?.length]);

  if (!progress) return null;

  const { runName, messages = [], progress: pct, stage, status, error } = progress;
  const running = status === 'running';
  const failed = status === 'failed';
  const done = status === 'completed';
  const showBar = typeof pct === 'number' && pct >= 0 && pct <= 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 shrink-0">
          {running && <Loader2 className="h-4 w-4 text-indigo-600 animate-spin shrink-0" />}
          {done && <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />}
          {failed && <XCircle className="h-4 w-4 text-red-600 shrink-0" />}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {running ? 'Test running' : done ? 'Test finished' : 'Test failed'}
            </h3>
            <p className="text-xs text-gray-500 truncate">{runName || 'Test run'}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        {showBar && (
          <div className="px-4 py-2 border-b border-gray-100 shrink-0">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{stageLabel(stage)}</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${failed ? 'bg-red-500' : done ? 'bg-green-500' : 'bg-indigo-600'}`}
                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
              />
            </div>
          </div>
        )}

        {failed && error && (
          <div className="mx-4 mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-2 shrink-0">
            {error}
          </div>
        )}

        {failed && /ECONNREFUSED|fetch|network|5001|AI service/i.test(error || '') && (
          <div className="mx-4 mt-2 flex gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2 shrink-0">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              Tests use the AI service over HTTP (port 5001), not a local Python shortcut.
              Start it manually: <code className="text-[11px] bg-amber-100/80 px-1 rounded">cd skeptics-ai-service</code> then{' '}
              <code className="text-[11px] bg-amber-100/80 px-1 rounded">.\venv312\Scripts\python main.py</code>.
              If Windows opens the Python install manager, disable the Store app execution aliases for python.exe.
            </p>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Activity log</p>
          {messages.length === 0 ? (
            <p className="text-sm text-gray-400">Waiting for updates…</p>
          ) : (
            <ul className="space-y-1">
              {messages.map((m, i) => {
                const isPass    = m.outcome === 'passed';
                const isFail    = m.outcome === 'failed';
                const isTimeout = m.outcome === 'timeout';
                const isError   = m.outcome === 'error';
                const isWarn    = m.outcome === 'warning';
                const isComplete = m.kind === 'run_complete';

                const rowBg = isPass ? 'bg-green-50/60' : (isFail || isError) ? 'bg-red-50/60' : (isTimeout || isWarn) ? 'bg-amber-50/60' : isComplete ? 'bg-blue-50/60' : '';
                const textColor = isPass ? 'text-green-800' : (isFail || isError) ? 'text-red-800' : (isTimeout || isWarn) ? 'text-amber-800' : isComplete ? 'text-blue-800' : 'text-gray-700';
                const stageColor = isPass ? 'text-green-600' : (isFail || isError) ? 'text-red-600' : (isTimeout || isWarn) ? 'text-amber-600' : 'text-indigo-600';

                return (
                  <li key={i} className={`text-xs flex gap-2 px-1 py-0.5 rounded ${rowBg}`}>
                    <span className="text-gray-400 shrink-0 font-mono whitespace-nowrap">{m.time}</span>
                    <span className={textColor}>
                      {m.stage && m.stage !== m.message && (
                        <span className={`font-medium mr-1 ${stageColor}`}>[{stageLabel(m.stage)}]</span>
                      )}
                      {m.message}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <div ref={logEndRef} />
        </div>

        <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-500 shrink-0">
          {running
            ? 'You can close this window — the test keeps running in the background.'
            : done
            ? 'Opening results…'
            : 'Close to return to the test runs list.'}
        </div>
      </div>
    </div>
  );
}
